import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { ZoomIn } from 'lucide-react';
import Card from '../shared/Card';
import Button from '../Button';
import Price from '../Price';
import { Badge } from '../Badge';
import ColorSwatch from '../ColorSwatch';
import './OrderCard.css';

/* A line's artwork: a 64px tile beside the text, or — `featured` — a full
   width well above it (the garment render), with an optional zoom button */
const LineArt = ({ line }) => (
  <span
    className={[
      'ds-order-card__thumb',
      line.featured && 'ds-order-card__thumb--featured',
      line.imageBackground && 'ds-order-card__thumb--backdrop',
    ].filter(Boolean).join(' ')}
    style={line.imageBackground ? { '--ds-order-card-thumb-bg': line.imageBackground } : undefined}
    aria-hidden={line.featured && line.onZoom ? undefined : 'true'}
  >
    {line.image && <img src={line.image} alt="" draggable="false" />}
    {line.featured && line.onZoom && (
      <Button
        size="tertiary"
        variant="text"
        iconOnly
        iconLeft={<ZoomIn size={20} strokeWidth={1.5} aria-hidden="true" />}
        onClick={line.onZoom}
        aria-label={line.zoomLabel || 'Zoom'}
        className="ds-order-card__zoom"
      />
    )}
  </span>
);

LineArt.propTypes = { line: PropTypes.object.isRequired };

/**
 * OrderCard — one order as a card: a head (number, date / summary, a status
 * Button / Badge), one row per line (artwork, title, a type Badge, a swatch + meta,
 * the DS Price) and the total. A `featured` line (the order's main product)
 * shows its artwork full width above its text, with an optional zoom. Pure
 * presentation — the page maps its order into `lines` (copy, formatting,
 * prices come from the page / API). Figma OVER-LL «Summary/My Orders» 3047:44098.
 */
const OrderCard = ({
  title,
  meta,
  status,
  lines = [],
  linesLabel,
  totalLabel,
  total,
  className = '',
}) => {
  const headingId = useId();
  return (
    <Card as="article" className={['ds-order-card', className].filter(Boolean).join(' ')} aria-labelledby={headingId}>
      <header className="ds-order-card__head">
        <div className="ds-order-card__head-text">
          <h4 id={headingId} className="ds-order-card__title typo-h4">{title}</h4>
          {meta && <p className="ds-order-card__meta typo-body-sm">{meta}</p>}
        </div>
        {status && status.onClick && (
          <Button
            size="tertiary"
            variant="gray"
            iconLeft={status.icon}
            onClick={status.onClick}
            aria-label={status.ariaLabel}
            className="ds-order-card__status"
          >
            {status.label}
          </Button>
        )}
        {status && !status.onClick && (
          <Badge variant={status.variant || 'neutral'} icon={status.icon} className="ds-order-card__status">
            {status.label}
          </Badge>
        )}
      </header>

      <ul className="ds-order-card__lines" aria-label={linesLabel}>
        {lines.map((line) => (
          <li key={line.id} className={`ds-order-card__line${line.featured ? ' ds-order-card__line--featured' : ''}`}>
            <LineArt line={line} />
            <div className="ds-order-card__line-info">
              <p className="ds-order-card__line-title typo-h5">{line.title}</p>
              {(line.chip || line.meta || line.swatch) && (
                <p className="ds-order-card__line-meta typo-body-sm">
                  {line.chip && <Badge size="s" shape="square">{line.chip}</Badge>}
                  {(line.meta || line.swatch) && (
                    <span className="ds-order-card__detail">
                      {line.swatch && <ColorSwatch color={line.swatch} />}
                      {line.meta}
                    </span>
                  )}
                </p>
              )}
            </div>
            {line.price && (
              <Price
                type="regular"
                state={line.originalPrice ? 'discounted' : 'default'}
                price={line.price}
                originalPrice={line.originalPrice || ''}
                className="ds-order-card__line-price"
              />
            )}
          </li>
        ))}
      </ul>

      {total && (
        <footer className="ds-order-card__foot">
          <span className="typo-h5">{totalLabel}</span>
          <Price type="regular" price={total} />
        </footer>
      )}
    </Card>
  );
};

OrderCard.propTypes = {
  /** Order number / name (H4) */
  title: PropTypes.node.isRequired,
  /** Date · summary line under the title */
  meta: PropTypes.node,
  /** Status: { label, icon, onClick, ariaLabel, variant }. With `onClick` a DS Button Tertiary/Gray (Figma 3047:44349 —
   *  opens the order's details); without, a Badge in `variant` */
  status: PropTypes.shape({
    label: PropTypes.node.isRequired,
    variant: PropTypes.oneOf(['neutral', 'success', 'warning', 'error', 'info']),
    icon: PropTypes.node,
    onClick: PropTypes.func,
    ariaLabel: PropTypes.string,
  }),
  /** One row per line: { id, image, imageBackground (CSS, the artwork's well), featured (artwork full width above the text), onZoom + zoomLabel (featured only: the zoom button), title, chip (type Badge), swatch (CSS colour), meta, price, originalPrice } — prices preformatted */
  lines: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    image: PropTypes.string,
    imageBackground: PropTypes.string,
    featured: PropTypes.bool,
    onZoom: PropTypes.func,
    zoomLabel: PropTypes.string,
    title: PropTypes.node.isRequired,
    chip: PropTypes.node,
    swatch: PropTypes.string,
    meta: PropTypes.node,
    price: PropTypes.string,
    originalPrice: PropTypes.string,
  })),
  /** Accessible name of the lines list */
  linesLabel: PropTypes.string,
  totalLabel: PropTypes.node,
  /** Preformatted total */
  total: PropTypes.string,
  className: PropTypes.string,
};

export default OrderCard;
